import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { AnnotationType } from 'webapp/enums/enums';

import AnnotationFormComponent from 'webapp/components/annotation/form';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { MetricSetup } from 'webapp/models-custom';
import Annotation from 'webapp/models-custom/annotation';
import type { AnnotationData } from 'webapp/models-custom/annotation';
import type Block from 'webapp/models-custom/block';

type AnnotateMode = 'list' | 'add' | 'edit';

interface Args {
	blockM: Block | null;
	metricSetup: MetricSetup;
	isLoading?: boolean;
}

export default class MetricDetailsSettingsAnnotateComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('annotations') declare annotationService: Service.Annotations;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	@tracked mode: AnnotateMode = 'list';
	@tracked editingAnnotation: Annotation | null = null;
	@tracked submitInProgress = false;

	get timezone(): string {
		return this.mmSourcesService.defaultTimezone;
	}

	get annotationsData(): AnnotationData[] {
		return this.args.blockM?.sample?.sampledata?.annotations || [];
	}

	@cached
	get annotations(): Annotation[] {
		return this.annotationsData
			.filter((a) => a.type === AnnotationType.DEFAULT)
			.map((a) => Annotation.createFromModel(a, this.timezone))
			.sort((a, b) => b.timestamp - a.timestamp);
	}

	get isListEmpty(): boolean {
		return this.annotations.length === 0;
	}

	get isListMode(): boolean {
		return this.mode === 'list';
	}

	get isFormMode(): boolean {
		return this.mode === 'add' || this.mode === 'edit';
	}

	get formTitle(): string {
		return this.mode === 'edit' ? 'Edit annotation' : 'Add annotation';
	}

	get showDeleteInForm(): boolean {
		return this.mode === 'edit';
	}

	get isCheckboxEditDisabled(): boolean {
		return this.submitInProgress || this.mode === 'edit';
	}

	@action
	startAdd(): void {
		const metricSetupData = this.args.metricSetup.metricSetupData;
		const dataPointInfo = {
			timestamp: Math.floor(Date.now() / 1000),
			metricSetupData: metricSetupData || undefined,
		};

		this.editingAnnotation = Annotation.createNew(dataPointInfo, this.timezone);
		this.mode = 'add';
	}

	@action
	startEdit(annotation: Annotation): void {
		this.editingAnnotation = annotation;
		this.mode = 'edit';
	}

	@action
	handleSave(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const annotation = this.editingAnnotation;
		if (!annotation) return;

		if (!annotation.note) {
			if (annotation.isNew) {
				this.resetToList();
			} else {
				this.handleDelete();
			}
			return;
		}

		this.submitInProgress = true;

		const existingId = annotation.isNew ? null : annotation.id;

		annotation
			.save()
			.then(() => {
				this.updateSampleAnnotations(existingId, annotation);
				this.resetToList();
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	handleDelete(): void {
		const annotation = this.editingAnnotation;
		if (!annotation || annotation.isNew) return;

		this.submitInProgress = true;

		const id = annotation.id;

		annotation
			.delete()
			.then(() => {
				this.updateSampleAnnotations(id, null);
				this.resetToList();
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	handleCancel(): void {
		this.resetToList();
	}

	private updateSampleAnnotations(id: number | null, model: Annotation | null): void {
		const sample = this.args.blockM?.sample;
		if (!sample) return;

		this.annotationService.updateSampleAnnotations(sample, id, model);
	}

	private resetToList(): void {
		this.mode = 'list';
		this.editingAnnotation = null;
		this.submitInProgress = false;
	}

	<template>
		{{#if @isLoading}}
			<div class="metric-explorer-settings__skeleton">
				<div class="metric-explorer-settings__skeleton-line row-1"></div>
				<div class="metric-explorer-settings__skeleton-line row-2"></div>
				<div class="metric-explorer-settings__skeleton-line row-3"></div>
				<div class="metric-explorer-settings__skeleton-line row-4"></div>
			</div>
		{{else if this.isListMode}}
			{{#if this.isListEmpty}}
				<div class="settings-annotate__empty">
					<p class="settings-annotate__empty-text">
						There are no annotations within the selected date range. Change your date
						range or create a new annotation.
					</p>
				</div>
			{{else}}
				<div class="settings-annotate__list">
					{{#each this.annotations as |annotation|}}
						<div class="settings-annotate__item">
							<div class="settings-annotate__item-content">
								<div class="settings-annotate__item-header">
									<AvatarOrInitialsComponent
										@name={{annotation.displayName}}
										@avatar={{annotation.user.avatar}}
									/>
									<span class="settings-annotate__item-author">
										{{annotation.displayName}}
									</span>
									{{#if annotation.displayTime}}
										<span class="settings-annotate__item-date">
											{{annotation.displayTime}}
										</span>
									{{/if}}
								</div>
								<p class="settings-annotate__item-note">
									{{annotation.note}}
								</p>
							</div>
							<button
								type="button"
								class="settings-annotate__edit-btn"
								{{on "click" (fn this.startEdit annotation)}}
							>
								<CommonUiIconComponent @name="Pencil" @size="sm" />
							</button>
						</div>
					{{/each}}
				</div>
			{{/if}}

			<SubmitButtonComponent
				@theme="blue-outlined"
				@text="New annotation"
				@icon="Plus"
				@onTrigger={{this.startAdd}}
			/>
		{{else if this.isFormMode}}
			<div class="settings-annotate__form-header">
				<CommonUiIconComponent @name="AnnotationAdd" @size="md" />
				<span class="settings-annotate__form-title">{{this.formTitle}}</span>
			</div>

			{{#if this.editingAnnotation}}
				<AnnotationFormComponent
					@model={{this.editingAnnotation}}
					@submitInProgress={{this.submitInProgress}}
					@onSave={{this.handleSave}}
					@onCancel={{this.handleCancel}}
					@onDelete={{if this.showDeleteInForm this.handleDelete}}
					@isCheckboxEditDisabled={{this.isCheckboxEditDisabled}}
				/>
			{{/if}}
		{{/if}}
	</template>
}
